import type { Thread } from "../../types";
import { scopeThreadRef } from "@cmmd-forge/client-runtime";
import { useEffect, useState } from "react";
import { useStore } from "../../store";
import { createThreadSelectorByRef } from "../../storeSelectors";
import { useComposerDraftStore } from "../../composerDraftStore";
import { useComposerHandleContext } from "../../composerHandleContext";
import { readEnvironmentApi } from "../../environmentApi";
import { retainThreadDetailSubscription } from "../../environments/runtime/service";
import { newCommandId } from "../../lib/utils";
import type { ChatComposerProps } from "./ChatComposer";
import { WorktreeSetupCard } from "./WorktreeSetupCard";
import type { RuntimeAdmissionState } from "./useRuntimeAdmission";
import { buildAdmissionLaunchProgress, hasAgentWorkSince } from "./admissionLaunchProgress";
import {
  deriveWorktreeLaunchProgressFromStoredActivities,
  type WorktreeLaunchProgress,
} from "./worktreeLaunchProgress";

type Props = Readonly<
  Pick<
    ChatComposerProps,
    "environmentId" | "activeThreadId" | "activeThread" | "composerDraftTarget" | "isSendBusy"
  >
> & {
  readonly runtimeAdmission?: RuntimeAdmissionState | null;
  /** Admission is rendered in the transcript for the loaded thread surface. */
  readonly showAdmission?: boolean;
};

/** Shows real launch progress in the composer and synthetic admission only when requested. */
export function WorktreeLaunchPanel(props: Props) {
  const ref = props.activeThreadId
    ? scopeThreadRef(props.environmentId, props.activeThreadId)
    : null;
  const storedThread = useStore(createThreadSelectorByRef(ref));
  const thread = storedThread ?? props.activeThread;
  const threadId = storedThread?.id;
  useEffect(() => {
    if (!threadId) return;
    return retainThreadDetailSubscription(props.environmentId, threadId);
  }, [props.environmentId, threadId]);
  const draftSession = useComposerDraftStore((store) =>
    typeof props.composerDraftTarget === "string"
      ? store.getDraftSession(props.composerDraftTarget)
      : store.getDraftSessionByRef(props.composerDraftTarget),
  );
  const derived = threadLaunchProgress(thread);
  const admitting = admittingBeforeRealProgress(
    props.runtimeAdmission,
    derived,
    thread?.latestTurn?.startedAt,
    thread?.messages,
  );
  const progress =
    props.showAdmission === false
      ? derived
      : admitting
        ? buildAdmissionLaunchProgress(admitting)
        : derived;
  if (
    !progress ||
    progress.phase === "done" ||
    (draftSession?.envMode === "local" && progress.phase !== "running")
  )
    return null;

  return (
    <ActiveLaunchPanel
      {...props}
      progress={progress}
      cancelOverride={admitting ? (props.runtimeAdmission?.onCancel ?? null) : null}
      canUseProjectCheckout={Boolean(draftSession) && !thread?.worktreePath}
    />
  );
}

function threadLaunchProgress(thread: Thread | undefined): WorktreeLaunchProgress | null {
  return deriveWorktreeLaunchProgressFromStoredActivities(
    thread?.activities ?? [],
    thread?.latestTurn?.startedAt ?? null,
    thread?.latestTurn?.completedAt &&
      (thread.latestTurn.state === "error" || thread.latestTurn.state === "interrupted")
      ? { state: thread.latestTurn.state, completedAt: thread.latestTurn.completedAt }
      : undefined,
  );
}

/**
 * Whether to show the synthetic pre-activity admission card: before any real
 * worktree.* activity exists yet, and before the current turn has actually
 * started. A real activity stream always wins once it exists, so the
 * synthetic card never outlives the real one.
 */
function admittingBeforeRealProgress(
  admission: RuntimeAdmissionState | null | undefined,
  derived: WorktreeLaunchProgress | null,
  latestTurnStartedAt: string | null | undefined,
  messages: ReadonlyArray<Pick<Thread["messages"][number], "role" | "createdAt">> | undefined,
): RuntimeAdmissionState | null {
  if (!admission) return null;
  if (latestTurnStartedAt && latestTurnStartedAt >= admission.startedAt) return null;
  if (derived && derived.startedAt >= admission.startedAt) return null;
  if (messages && hasAgentWorkSince(messages, admission.startedAt)) return null;
  return admission;
}

function useLaunchAction() {
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const act = async (action: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setActionError(null);
    try {
      await action();
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "The launch action failed. Try again.",
      );
    } finally {
      setBusy(false);
    }
  };
  return { busy, actionError, act };
}

function ActiveLaunchPanel(
  props: Props & {
    readonly progress: WorktreeLaunchProgress;
    readonly cancelOverride: (() => void) | null;
    readonly canUseProjectCheckout: boolean;
  },
) {
  const { progress, cancelOverride } = props;
  const ref = props.activeThreadId
    ? scopeThreadRef(props.environmentId, props.activeThreadId)
    : null;
  const composer = useComposerHandleContext();
  const { busy, actionError, act } = useLaunchAction();
  const editMessage = () => {
    useComposerDraftStore.getState().clearStagedComposerSubmission(props.composerDraftTarget);
    composer?.current?.focusAtEnd();
  };
  const canRecover = canRecoverLaunch(props.isSendBusy, progress.phase);
  const api = readEnvironmentApi(props.environmentId);

  return (
    <WorktreeSetupCard
      progress={progress}
      busy={busy}
      actionError={actionError}
      onCancel={
        cancelOverride ??
        (progress.phase === "running" && api && ref
          ? () => {
              void act(async () => {
                await api.orchestration.dispatchCommand({
                  type: "thread.turn.interrupt",
                  threadId: ref.threadId,
                  commandId: newCommandId(),
                  createdAt: new Date().toISOString(),
                });
              });
            }
          : null)
      }
      onEditMessage={canRecover ? editMessage : null}
      onUseProjectCheckout={
        canRecover && props.canUseProjectCheckout
          ? () => {
              void act(async () => {
                useComposerDraftStore
                  .getState()
                  .setDraftThreadContext(props.composerDraftTarget, { envMode: "local" });
                editMessage();
              });
            }
          : null
      }
    />
  );
}

function canRecoverLaunch(isSendBusy: boolean, phase: WorktreeLaunchProgress["phase"]): boolean {
  return !isSendBusy && (phase === "failed" || phase === "cancelled");
}
